// reportAppendix.js — shared PDF-appendix logic for the FINAL report package, used by BOTH the office
// PackageViewer and the client endorsement page so they render and print the SAME complete document.
// Appendix PDFs are rasterised to page images (pdf.js, loaded at runtime from cdnjs — no build dep) and
// appended inline. Landscape pages are rotated 90° for print (legible on a portrait page); native on screen.
import { supabase } from './supabase'

// SINGLE SOURCE OF TRUTH for report photo/document URLs. Every viewer (office PackageViewer, client
// endorsement page) resolves inline media from the SAME frozen snapshot evidence list via THIS function —
// never from live audit_evidence_files — so the report shows identical photos before and after approval/
// signing, and archival/re-upload drift can no longer make a photo silently disappear. Returns
// { storage_path → signed URL }; TTL is long enough to survive a review-then-print session.
const MEDIA_TYPES = e => /^rcm:/.test(e?.evidence_type || '') ||
  ['rcm_document', 'evidence_pack', 'audit_working_document', 'manual_checklist', 'manual_interview'].includes(e?.evidence_type)

// `signer`: optional async (storage_path) => signed URL. PUBLIC pages (anon) cannot sign the PRIVATE
// audit-evidence bucket client-side, so they pass a server-side (service-role, token-verified) signer
// — see the report-ack-media edge function. Authenticated office pages omit it and sign directly.
export async function resolveSnapshotMedia(evidence, ttl = 3600, signer = null) {
  const paths = [...new Set((evidence || []).filter(e => e?.storage_path && MEDIA_TYPES(e)).map(e => e.storage_path))]
  const media = {}
  await Promise.all(paths.map(async p => {
    try {
      const url = signer ? await signer(p) : (await supabase.storage.from('audit-evidence').createSignedUrl(p, ttl)).data?.signedUrl
      if (url) media[p] = url
    } catch (e) {}
  }))
  return media
}

let pdfjsPromise = null
export function loadPdfJs() {
  if (typeof window !== 'undefined' && window.pdfjsLib) return Promise.resolve(window.pdfjsLib)
  if (!pdfjsPromise) {
    pdfjsPromise = new Promise((resolve, reject) => {
      const sc = document.createElement('script')
      sc.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
      sc.onload = () => { window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'; resolve(window.pdfjsLib) }
      sc.onerror = reject
      document.body.appendChild(sc)
    })
  }
  return pdfjsPromise
}

// Rasterisation settings: target scale 3 ≈ 216 DPI on an A4 page (was 1.5 ≈ 108 DPI, which turned dense
// small-text attachments — e.g. manual Excel checklists — near-unreadable in the official package).
// MAX_EDGE caps the rendered long side in pixels: large-format source pages (Excel exports can be huge)
// get the sharpest render that FITS instead of exploding the package past the storage upload limit
// ("The object exceeded the maximum allowed size" — Mentrade, Aug 2026). Quality 0.82 balances the same.
const RASTER_SCALE = 3
const RASTER_QUALITY = 0.82
const MAX_EDGE = 2400

export async function rasterisePdf(url) {
  const lib = await loadPdfJs()
  // isEvalSupported:false — pdf.js's official fix for CVE-2024-4367 (a crafted PDF font could run code in the page).
  const pdf = await lib.getDocument({ url, isEvalSupported: false }).promise
  const pages = []
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const base1 = page.getViewport({ scale: 1 })
    const scale = Math.min(RASTER_SCALE, MAX_EDGE / Math.max(base1.width, base1.height))
    const base = page.getViewport({ scale })
    const landscape = base.width > base.height
    const c1 = document.createElement('canvas'); c1.width = base.width; c1.height = base.height
    await page.render({ canvasContext: c1.getContext('2d'), viewport: base }).promise
    const screen = c1.toDataURL('image/jpeg', RASTER_QUALITY)
    let print = screen
    if (landscape) {
      const vpR = page.getViewport({ scale, rotation: 90 })
      const c2 = document.createElement('canvas'); c2.width = vpR.width; c2.height = vpR.height
      await page.render({ canvasContext: c2.getContext('2d'), viewport: vpR }).promise
      print = c2.toDataURL('image/jpeg', RASTER_QUALITY)
    }
    pages.push({ screen, print, landscape })
  }
  return pages
}

// Appendix PDFs in report-section order. `evidence` = [{ evidence_type, storage_path, file_name }] (live
// office files OR the snapshot's frozen evidence list — both shapes match). Covers manual AND system audits.
const APPENDIX_ORDER = [
  ['rcm_document', 'Section 4 — Implementation Document'],
  ['manual_checklist', 'Section 5 — RM Audit Checklist (manual/Excel)'],
  ['manual_interview', 'Section 5 — Interview Personnel Sheet (manual/Excel)'],
]
export function buildAppendixList(evidence) {
  // Skip an exact repeat (same slot + same file name) — the SAME rule ReportDocument applies to the report body,
  // so the appended pages always match what the body describes (an accidental double upload never repeats pages).
  const seen = new Set()
  const list = (evidence || []).filter(Boolean).filter(e => {
    const k = `${e.evidence_type || ''}|${e.file_name || ''}`
    if (seen.has(k)) return false
    seen.add(k); return true
  })
  const out = []
  // Each slot may hold MORE THAN ONE PDF (the auditor can upload several). Append them ALL, in upload order,
  // so the report contains every page — multiple PDFs in a slot read back-to-back as one continuous section.
  const pushAll = (evs, label) => {
    const withFile = evs.filter(e => e?.storage_path)
    withFile.forEach((ev, i) => out.push({ label: withFile.length > 1 ? `${label} (${i + 1} of ${withFile.length})` : label, ev }))
  }
  for (const [type, label] of APPENDIX_ORDER) pushAll(list.filter(e => e.evidence_type === type), label)
  // Supporting evidence pack — prefer evidence_pack, then audit_working_document; include all of both.
  pushAll([...list.filter(e => e.evidence_type === 'evidence_pack'), ...list.filter(e => e.evidence_type === 'audit_working_document')], 'Supporting Evidence Pack')
  return out
}

// Print HTML for the appendices. GUARANTEE: one source PDF page = exactly one report page — never split,
// no trailing fragments, no blank pages. How it's guaranteed:
//   • EACH page gets its own `page-break-before:always` block → every source page starts at the top of a
//     fresh report page (landscape uses the pre-rotated `print` image so it fills the sheet — no CSS rotate).
//   • The image is bounded by `max-height:235mm`, which is safely under the printable height of A4 (269mm)
//     AND US Letter (251mm) even with the section label — so an image that starts at the top of a page can
//     never reach the bottom, hence can never spill onto a second page.
//   • No fixed-height wrapper and no overflow:hidden → nothing can be clipped; short pages simply leave
//     whitespace below (that is not a blank PAGE). `page-break-inside:avoid` is a secondary safeguard only.
export function appendixPrintHtml(appendices) {
  return (appendices || []).filter(a => a.status === 'ready').map(a =>
    a.pages.map((p, pi) => {
      // The section label lives on the FIRST page and must stay with its first image. That page's image is
      // capped a little shorter (210mm) so label + image comfortably fit ONE page on A4 AND US Letter — the
      // heading is never stranded alone. Later pages use the full 235mm. 1:1 (one source page = one report
      // page) is preserved either way.
      const maxH = pi === 0 ? '210mm' : '235mm'
      return `<div style="page-break-before:always;page-break-inside:avoid;break-inside:avoid;text-align:center">` +
        (pi === 0 ? `<div style="font-weight:700;font-size:12px;margin:0 0 6px;text-align:left">${a.label}</div>` : '') +
        `<img src="${p.print}" style="display:inline-block;max-width:100%;max-height:${maxH};object-fit:contain" />` +
        `</div>`
    }).join('')
  ).join('')
}
